import { jsxs, jsx } from 'react/jsx-runtime';
import React__default, { useState, useRef, useEffect, useCallback } from 'react';
import classNames from '../../../node_modules/.pnpm/classnames@2.5.1/node_modules/classnames/index.js';
import usePrevious from '../../hooks/usePrevious.js';
import style from './TOTPCodeInput.module.css.js';

const CODE_LENGTH = 6;
const TOTPCodeInput = ({ error = false, errorMessage, disabled = false, autoFocus = true, length = CODE_LENGTH, className, onBlur, onChange, onComplete, }) => {
    const [values, setValues] = useState(Array(length).fill(""));
    const previousValues = usePrevious(values);
    const inputRefs = useRef([]);
    useEffect(() => {
        if (previousValues?.join("") === values.join("")) {
            return;
        }
        const code = values.join("");
        onChange?.(code);
        if (values.join("").length === length) {
            onComplete?.(code);
        }
    }, [values, previousValues, length, onChange, onComplete]);
    const handleFocusField = useCallback((operation) => {
        setTimeout(() => {
            let targetIndex = -1;
            if (operation.type === "first") {
                targetIndex = 0;
            }
            else if (operation.type === "next" && operation.index !== undefined) {
                targetIndex = Math.min(operation.index + 1, length - 1);
            }
            else if (operation.type === "previous" && operation.index !== undefined) {
                targetIndex = Math.max(operation.index - 1, 0);
            }
            else if (operation.type === "index" && operation.index !== undefined) {
                targetIndex = operation.index;
            }
            else if (operation.type === "first-empty") {
                const firstEmptyIndex = values.findIndex((v) => !v);
                targetIndex = firstEmptyIndex !== -1 ? firstEmptyIndex : length - 1;
            }
            else if (operation.type === "current-if-filled" && operation.index !== undefined) {
                if (values[operation.index]) {
                    targetIndex = operation.index;
                }
                else {
                    const firstEmptyIndex = values.findIndex((v) => !v);
                    targetIndex = firstEmptyIndex !== -1 ? firstEmptyIndex : length - 1;
                }
            }
            if (targetIndex >= 0 && targetIndex < length && inputRefs.current[targetIndex]) {
                inputRefs.current[targetIndex]?.focus();
            }
        }, 0);
    }, [values, length]);
    useEffect(() => {
        if (values.join("") === "" && autoFocus && !disabled) {
            handleFocusField({ type: "first" });
        }
    }, [values, autoFocus, disabled, handleFocusField]);
    const handleChange = useCallback((operation) => {
        const newValues = [...values];
        if (operation.type === "insert") {
            const digit = operation.value?.slice(-1).replace(/\D/g, "") || "";
            if (digit) {
                newValues[operation.index] = digit;
            }
            else {
                return;
            }
        }
        else if (operation.type === "delete") {
            const deleteIndex = operation.index;
            if (deleteIndex >= 0 && deleteIndex < length) {
                newValues[deleteIndex] = "";
            }
        }
        else if (operation.type === "paste") {
            const digits = (operation.value || "").replace(/\D/g, "").slice(0, length).split("");
            digits.forEach((digit, i) => {
                const targetIndex = operation.index + i;
                if (targetIndex < length) {
                    newValues[targetIndex] = digit;
                }
            });
        }
        else if (operation.type === "clear-range") {
            const start = operation.index;
            const end = operation.count !== undefined ? start + operation.count : length;
            for (let i = start; i < end && i < length; i++) {
                newValues[i] = "";
            }
        }
        let finalValues;
        let nextFocusIndex;
        // Shift values left for delete operations to remove gaps
        if (operation.type === "delete" || operation.type === "clear-range") {
            const shiftedValues = Array(length).fill("");
            let writeIndex = 0;
            for (let i = 0; i < length; i++) {
                if (newValues[i]) {
                    shiftedValues[writeIndex] = newValues[i];
                    writeIndex++;
                }
            }
            finalValues = shiftedValues;
            nextFocusIndex = writeIndex < length ? writeIndex : length - 1;
        }
        else {
            finalValues = newValues;
            if (operation.type === "insert") {
                // Always move to next field after insert
                nextFocusIndex = Math.min(operation.index + 1, length - 1);
            }
            else if (operation.type === "paste") {
                // Focus first empty after paste
                const firstEmptyIndex = finalValues.findIndex((v) => !v);
                nextFocusIndex = firstEmptyIndex !== -1 ? firstEmptyIndex : length - 1;
            }
            else {
                nextFocusIndex = operation.index;
            }
        }
        setValues(finalValues);
        // Handle focus based on operation type
        if (operation.type === "insert" || operation.type === "paste") {
            handleFocusField({ type: "index", index: nextFocusIndex });
        }
    }, [values, length, handleFocusField]);
    const handleKeyDown = useCallback((index, e) => {
        // Use metaKey for Mac (Cmd key) and ctrlKey for Windows/Linux
        const isModKey = e.metaKey || e.ctrlKey;
        // Cmd/Ctrl + Arrow: Jump to start/end
        if (isModKey && e.key === "ArrowLeft") {
            e.preventDefault();
            handleFocusField({ type: "first" });
            return;
        }
        if (isModKey && e.key === "ArrowRight") {
            e.preventDefault();
            handleFocusField({ type: "first-empty" });
            return;
        }
        // Cmd/Ctrl + Delete: Delete all fields to the right
        if (isModKey && e.key === "Delete") {
            e.preventDefault();
            handleChange({ type: "clear-range", index, count: length - index });
            return;
        }
        // Cmd/Ctrl + Backspace: Delete all fields to the left
        if (isModKey && e.key === "Backspace") {
            e.preventDefault();
            handleChange({ type: "clear-range", index: 0, count: index + 1 });
            handleFocusField({ type: "first" });
            return;
        }
        // Delete: Delete current field, shift digits left
        if (e.key === "Delete" && !isModKey) {
            e.preventDefault();
            if (values[index]) {
                handleChange({ type: "delete", index });
            }
            return;
        }
        // Backspace: Delete current field, shift digits left, move focus left
        if (e.key === "Backspace" && !isModKey) {
            e.preventDefault();
            if (values[index]) {
                handleChange({ type: "delete", index });
                handleFocusField({ type: "previous", index });
            }
            else if (index > 0) {
                handleChange({ type: "delete", index: index - 1 });
                handleFocusField({ type: "previous", index });
            }
            return;
        }
        if (e.key === "ArrowLeft") {
            e.preventDefault();
            handleFocusField({ type: "previous", index });
        }
        else if (e.key === "ArrowRight") {
            e.preventDefault();
            if (values[index]) {
                handleFocusField({ type: "next", index });
            }
        }
    }, [values, length, handleChange, handleFocusField]);
    const handlePaste = useCallback((index, e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData("text").replace(/\D/g, "");
        if (pastedData.length > 0) {
            handleChange({ type: "paste", index, value: pastedData });
        }
    }, [handleChange]);
    const handleInputFocus = useCallback((_, e) => {
        e.target.select();
    }, []);
    const handleBlur = useCallback(() => {
        onBlur?.();
    }, [onBlur]);
    return (jsxs("div", { className: classNames(style.wrapper, className), children: [jsx("div", { className: style.inputContainer, children: Array.from({ length }).map((_, index) => (jsxs(React__default.Fragment, { children: [jsx("input", { ref: (el) => {
                                inputRefs.current[index] = el;
                            }, type: "text", inputMode: "numeric", pattern: "[0-9]", value: values[index] || "", onChange: (e) => {
                                const value = e.target.value;
                                const digit = value.replace(/\D/g, "").slice(-1);
                                if (digit) {
                                    handleChange({ type: "insert", index, value: digit });
                                }
                                else if (value === "") {
                                    // Handle when user clears the field manually
                                    handleChange({ type: "delete", index });
                                }
                            }, onKeyDown: (e) => handleKeyDown(index, e), onPaste: (e) => handlePaste(index, e), onFocus: (e) => handleInputFocus(index, e), onMouseDown: (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                // Find the first empty field
                                const firstEmptyIndex = values.findIndex((v) => !v);
                                // If clicking on an empty field beyond the first empty position, focus the first empty instead
                                if (firstEmptyIndex !== -1 && index > firstEmptyIndex && !values[index]) {
                                    handleFocusField({ type: "index", index: firstEmptyIndex });
                                }
                                else {
                                    handleFocusField({ type: "index", index });
                                }
                            }, onBlur: handleBlur, disabled: disabled, className: classNames(style.input, error && style.error), "aria-label": `Digit ${index + 1} of ${length}` }), index === Math.floor(length / 2) - 1 && jsx("span", { className: style.separator, children: "-" })] }, index))) }), errorMessage && jsx("span", { className: style.errorMessage, children: errorMessage })] }));
};

export { TOTPCodeInput };
//# sourceMappingURL=TOTPCodeInput.js.map
